import React, { useEffect, useMemo, useState } from "react";
import { useMsal } from "@azure/msal-react";
import DataTable, { TableColumn } from "react-data-table-component";
import { Euro, Download, Calendar, Info } from "lucide-react";
import * as XLSX from "xlsx";
import { getApiBaseUrl } from "../utils/baseUrl";
import { useLoading, acquireTokenSafe } from "../App";
import { AcquisitionChargebackEntry, AcquisitionSummary } from "../types";

// ── Helpers ──────────────────────────────────────────────────

const secToHours = (sec: number) => Math.round((sec / 3600) * 100) / 100;

const getStartOfWeek = (d: Date): Date => {
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.getFullYear(), d.getMonth(), diff);
};

const formatDateInput = (d: Date): string => d.toISOString().split("T")[0];

type DatePreset = "week" | "30days" | "ytd" | "custom";

const getDatesForPreset = (preset: DatePreset): { start: string; end: string } => {
    const now = new Date();
    const end = formatDateInput(now);
    switch (preset) {
        case "week": {
            const monday = getStartOfWeek(now);
            monday.setDate(monday.getDate() - 7);
            const sunday = new Date(monday);
            sunday.setDate(sunday.getDate() + 6);
            return { start: formatDateInput(monday), end: formatDateInput(sunday) };
        }
        case "30days": {
            const d = new Date(now);
            d.setDate(d.getDate() - 30);
            return { start: formatDateInput(d), end };
        }
        case "ytd":
            return { start: formatDateInput(new Date(now.getFullYear(), 0, 1)), end };
        default:
            return { start: formatDateInput(new Date(now.getFullYear(), now.getMonth(), 1)), end };
    }
};

// SKU → friendly name. Order drives KPI card and filter-chip order.
const ACQUISITIONS = [
    { sku: "KIN-AC-CG", name: "ClearGroup" },
    { sku: "KIN-AC-MAC", name: "Martin&Co" },
    { sku: "KIN-AC-OA", name: "OnlyAudit" },
] as const;
const ACQUISITION_NAME_BY_SKU: Record<string, string> = Object.fromEntries(
    ACQUISITIONS.map((a) => [a.sku, a.name])
);

const customStyles = {
    table: { style: { backgroundColor: "var(--bg-secondary)", borderRadius: "8px" } },
    headRow: {
        style: {
            backgroundColor: "var(--bg-tertiary)",
            borderBottomColor: "var(--border-color)",
            color: "var(--text-primary)",
            fontWeight: 600,
            fontSize: "0.85rem",
        },
    },
    headCells: { style: { color: "var(--text-secondary)", paddingLeft: "16px", paddingRight: "16px" } },
    rows: {
        style: {
            backgroundColor: "var(--bg-secondary)",
            color: "var(--text-primary)",
            borderBottomColor: "var(--border-color)",
        },
        highlightOnHoverStyle: {
            backgroundColor: "var(--bg-hover)",
            color: "var(--text-primary)",
            borderBottomColor: "var(--border-color)",
        },
    },
    cells: { style: { color: "var(--text-primary)", paddingLeft: "16px", paddingRight: "16px" } },
    pagination: {
        style: {
            backgroundColor: "var(--bg-secondary)",
            color: "var(--text-secondary)",
            borderTopColor: "var(--border-color)",
        },
        pageButtonsStyle: {
            color: "var(--text-secondary)",
            fill: "var(--text-secondary)",
            "&:hover:not(:disabled)": { backgroundColor: "var(--bg-hover)" },
            "&:disabled": { opacity: 0.4 },
        },
    },
    noData: { style: { backgroundColor: "var(--bg-secondary)", color: "var(--text-secondary)" } },
};

// ── Main Component ──────────────────────────────────────────

const AcquisitionChargebacks: React.FC = () => {
    const { instance, accounts } = useMsal();
    const { setLoading } = useLoading();

    const [entries, setEntries] = useState<AcquisitionChargebackEntry[]>([]);
    const [dateRange, setDateRange] = useState<DatePreset>("week");
    const [startDate, setStartDate] = useState(() => getDatesForPreset("week").start);
    const [endDate, setEndDate] = useState(() => getDatesForPreset("week").end);
    const [acquisitionFilter, setAcquisitionFilter] = useState<string>("all");
    const [showHelp, setShowHelp] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handlePresetChange = (preset: DatePreset) => {
        setDateRange(preset);
        if (preset !== "custom") {
            const { start, end } = getDatesForPreset(preset);
            setStartDate(start);
            setEndDate(end);
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            const token = await acquireTokenSafe(instance, accounts);
            if (!token) return;
            setLoading(true);
            setError(null);
            try {
                const url = `${getApiBaseUrl()}/api/acquisition-chargebacks/data?startDate=${startDate}&endDate=${endDate}`;
                const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
                if (!res.ok) throw new Error(`Request failed (${res.status})`);
                setEntries(await res.json());
            } catch (err: any) {
                setError(err?.message || "Failed to load data");
            } finally {
                setLoading(false);
            }
        };
        if (startDate && endDate) fetchData();
    }, [instance, accounts, startDate, endDate, setLoading]);

    // KPI summaries — seeded so all three cards render even when an
    // acquisition has zero entries in the selected range.
    const summaries: AcquisitionSummary[] = useMemo(() => {
        const map = new Map<string, AcquisitionSummary>();
        for (const a of ACQUISITIONS) {
            map.set(a.sku, { sku: a.sku, name: a.name, hours: 0, cost: 0, entryCount: 0 });
        }
        for (const e of entries) {
            const s = map.get(e.core_service_sku);
            if (!s) continue;
            const h = secToHours(e.duration || 0);
            s.hours += h;
            s.cost += e.billable_rate ? h * Number(e.billable_rate) : 0;
            s.entryCount += 1;
        }
        return ACQUISITIONS.map((a) => {
            const s = map.get(a.sku)!;
            return {
                ...s,
                hours: Math.round(s.hours * 100) / 100,
                cost: Math.round(s.cost * 100) / 100,
            };
        });
    }, [entries]);

    const totals = useMemo(() => {
        const t = summaries.reduce(
            (acc, s) => ({
                hours: acc.hours + s.hours,
                cost: acc.cost + s.cost,
                entryCount: acc.entryCount + s.entryCount,
            }),
            { hours: 0, cost: 0, entryCount: 0 }
        );
        return {
            hours: Math.round(t.hours * 100) / 100,
            cost: Math.round(t.cost * 100) / 100,
            entryCount: t.entryCount,
        };
    }, [summaries]);

    const filteredEntries = useMemo(
        () => (acquisitionFilter === "all" ? entries : entries.filter((e) => e.core_service_sku === acquisitionFilter)),
        [entries, acquisitionFilter]
    );

    type Row = AcquisitionChargebackEntry & {
        hours: number;
        rate: number;
        cost: number;
        acquisition: string;
        employeeName: string;
    };
    const rows: Row[] = useMemo(
        () =>
            filteredEntries.map((e) => {
                const h = secToHours(e.duration || 0);
                const rate = e.billable_rate ? Number(e.billable_rate) : 0;
                return {
                    ...e,
                    hours: h,
                    rate,
                    cost: rate ? Math.round(h * rate * 100) / 100 : 0,
                    acquisition: ACQUISITION_NAME_BY_SKU[e.core_service_sku] || e.core_service_sku,
                    employeeName: `${e.first_name || ""} ${e.last_name || ""}`.trim(),
                };
            }),
        [filteredEntries]
    );

    const columns: TableColumn<Row>[] = [
        {
            name: "Date",
            selector: (r) => r.start_time,
            sortable: true,
            cell: (r) => (
                <span className="cell-secondary">
                    {new Date(r.start_time).toLocaleDateString("en-IE")}
                </span>
            ),
        },
        {
            name: "Employee",
            selector: (r) => r.employeeName,
            sortable: true,
            grow: 2,
            cell: (r) => <span className="cell-primary">{r.employeeName || "—"}</span>,
        },
        {
            name: "Department",
            selector: (r) => r.department_title || "",
            sortable: true,
            cell: (r) => <span className="cell-secondary">{r.department_title || "—"}</span>,
        },
        {
            name: "Acquisition",
            selector: (r) => r.acquisition,
            sortable: true,
            cell: (r) => <span className="cell-secondary">{r.acquisition}</span>,
        },
        {
            name: "Client",
            selector: (r) => r.business_name || "",
            sortable: true,
            grow: 2,
            cell: (r) => <span className="cell-secondary">{r.business_name || "—"}</span>,
        },
        {
            name: "Task",
            selector: (r) => r.task_title || "",
            sortable: true,
            grow: 2,
            cell: (r) => <span className="cell-secondary">{r.task_title || "—"}</span>,
        },
        {
            name: "Hours",
            selector: (r) => r.hours,
            sortable: true,
            right: true,
            cell: (r) => <span className="cell-value">{r.hours.toFixed(2)}</span>,
        },
        {
            name: "Rate",
            selector: (r) => r.rate,
            sortable: true,
            right: true,
            cell: (r) => (
                <span className="cell-secondary">{r.rate > 0 ? `€${r.rate.toFixed(2)}` : "—"}</span>
            ),
        },
        {
            name: "Cost",
            selector: (r) => r.cost,
            sortable: true,
            right: true,
            cell: (r) => (
                <span className="cell-value accent">
                    {r.cost > 0
                        ? `€${r.cost.toLocaleString("en-IE", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                          })}`
                        : "—"}
                </span>
            ),
        },
        {
            name: "Comment",
            selector: (r) => r.comment || "",
            sortable: false,
            grow: 2,
            cell: (r) => (
                <span
                    className="cell-secondary"
                    style={{ maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                    title={r.comment || undefined}
                >
                    {r.comment || "—"}
                </span>
            ),
        },
    ];

    const downloadCSV = () => {
        const headers = [
            "Date",
            "Employee",
            "Email",
            "Department",
            "Acquisition",
            "SKU",
            "Client",
            "Task",
            "Hours",
            "Rate (EUR)",
            "Cost (EUR)",
            "Comment",
        ];
        const body = rows
            .map((r) =>
                [
                    r.start_time.split("T")[0],
                    `"${r.employeeName.replace(/"/g, '""')}"`,
                    r.email || "",
                    `"${(r.department_title || "").replace(/"/g, '""')}"`,
                    r.acquisition,
                    r.core_service_sku,
                    `"${(r.business_name || "").replace(/"/g, '""')}"`,
                    `"${(r.task_title || "").replace(/"/g, '""')}"`,
                    r.hours.toFixed(2),
                    r.rate > 0 ? r.rate.toFixed(2) : "",
                    r.cost.toFixed(2),
                    `"${(r.comment || "").replace(/"/g, '""')}"`,
                ].join(",")
            )
            .join("\n");
        const csv = `${headers.join(",")}\n${body}`;
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `acquisition_chargebacks_${startDate}_${endDate}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const downloadXLSX = () => {
        const wb = XLSX.utils.book_new();
        const summary = summaries.map((s) => ({
            Acquisition: s.name,
            SKU: s.sku,
            Hours: s.hours,
            "Cost (EUR)": s.cost,
            Entries: s.entryCount,
        }));
        summary.push({
            Acquisition: "Total",
            SKU: "",
            Hours: totals.hours,
            "Cost (EUR)": totals.cost,
            Entries: totals.entryCount,
        });
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(summary), "Summary");

        const detail = rows.map((r) => ({
            Date: r.start_time.split("T")[0],
            Employee: r.employeeName,
            Email: r.email || "",
            Department: r.department_title || "",
            Acquisition: r.acquisition,
            SKU: r.core_service_sku,
            Client: r.business_name || "",
            Task: r.task_title || "",
            Hours: r.hours,
            "Rate (EUR)": r.rate > 0 ? r.rate : null,
            "Cost (EUR)": r.cost,
            Comment: r.comment || "",
        }));
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(detail), "Entries");

        XLSX.writeFile(wb, `acquisition_chargebacks_${startDate}_${endDate}.xlsx`);
    };

    return (
        <div className="page-container">
            <div className="page-header compact">
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div className="page-title">
                        <Euro size={20} />
                        <h1>Acquisition Chargebacks</h1>
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                        <button
                            className="download-btn"
                            onClick={() => setShowHelp((v) => !v)}
                            title="Notes on Chargebacks"
                        >
                            <Info size={16} /> {showHelp ? "Hide details" : "Notes on Chargebacks"}
                        </button>
                        <button className="download-btn" onClick={downloadCSV} title="Export as CSV">
                            <Download size={16} /> CSV
                        </button>
                        <button className="download-btn" onClick={downloadXLSX} title="Export as XLSX">
                            <Download size={16} /> XLSX
                        </button>
                    </div>
                </div>
            </div>

            {showHelp && (
                <div
                    style={{
                        background: "var(--bg-secondary)",
                        border: "1px solid var(--border-color)",
                        borderLeft: "3px solid var(--accent-primary)",
                        borderRadius: 6,
                        padding: "14px 18px",
                        marginBottom: 20,
                        color: "var(--text-secondary)",
                        fontSize: 13,
                        lineHeight: 1.55,
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            marginBottom: 8,
                            color: "var(--text-primary)",
                            fontWeight: 600,
                            fontSize: 14,
                        }}
                    >
                        <Info size={16} /> How acquisition chargebacks are calculated
                    </div>
                    <p style={{ margin: "0 0 10px" }}>
                        This report isolates time logged against the three acquisition Master Services List SKUs
                        and translates it into a chargeback figure per acquisition.
                    </p>
                    <ul style={{ margin: "0 0 10px 20px", padding: 0 }}>
                        <li>
                            <strong style={{ color: "var(--text-primary)" }}>Scope.</strong> Only{" "}
                            <code>task_entries</code> whose <code>core_services.sku</code> is one of{" "}
                            {ACQUISITIONS.map((a, i) => (
                                <React.Fragment key={a.sku}>
                                    {i > 0 ? ", " : ""}
                                    <code>{a.sku}</code> ({a.name})
                                </React.Fragment>
                            ))}
                            , with status <code>submitted</code> or <code>approved</code>, within the selected date
                            range.
                        </li>
                        <li>
                            <strong style={{ color: "var(--text-primary)" }}>Hours</strong> = entry duration ÷ 3600.
                        </li>
                        <li>
                            <strong style={{ color: "var(--text-primary)" }}>Cost</strong> = hours ×{" "}
                            <code>admin_users.billable_rate</code> (the employee's rate from{" "}
                            <a href="/employee-rates" style={{ color: "var(--accent-primary)" }}>Employee Rates</a>
                            ). Entries by an employee with no rate set contribute <strong>0 to cost</strong> but
                            their hours still count.
                        </li>
                        <li>
                            <strong style={{ color: "var(--text-primary)" }}>KPI cards</strong> always show all
                            three acquisitions plus an overall total for the selected date range. The acquisition
                            chips filter the detail table only — they do not change the KPI figures.
                        </li>
                    </ul>
                </div>
            )}

            <div className="table-controls" style={{ marginBottom: 20 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    {(["week", "30days", "ytd", "custom"] as DatePreset[]).map((preset) => (
                        <button
                            key={preset}
                            className={`filter-select${dateRange === preset ? " active" : ""}`}
                            style={{
                                cursor: "pointer",
                                padding: "6px 12px",
                                border:
                                    dateRange === preset
                                        ? "1px solid var(--accent-primary)"
                                        : "1px solid var(--border-color)",
                                borderRadius: 6,
                                background:
                                    dateRange === preset ? "var(--accent-primary)" : "var(--bg-secondary)",
                                color: dateRange === preset ? "#fff" : "var(--text-secondary)",
                                fontWeight: dateRange === preset ? 600 : 400,
                                fontSize: 13,
                            }}
                            onClick={() => handlePresetChange(preset)}
                        >
                            {preset === "week"
                                ? "Past Week"
                                : preset === "30days"
                                ? "Last 30 Days"
                                : preset === "ytd"
                                ? "YTD"
                                : "Custom"}
                        </button>
                    ))}
                </div>

                {dateRange === "custom" && (
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <Calendar size={16} style={{ color: "var(--text-muted)" }} />
                        <input
                            type="date"
                            className="search-input"
                            style={{ minWidth: 150 }}
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                        />
                        <span style={{ color: "var(--text-muted)" }}>to</span>
                        <input
                            type="date"
                            className="search-input"
                            style={{ minWidth: 150 }}
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                        />
                    </div>
                )}

                <div style={{ marginLeft: "auto", display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {[{ sku: "all", name: "All" } as { sku: string; name: string }, ...ACQUISITIONS].map((a) => {
                        const active = acquisitionFilter === a.sku;
                        return (
                            <button
                                key={a.sku}
                                onClick={() => setAcquisitionFilter(a.sku)}
                                style={{
                                    padding: "6px 12px",
                                    borderRadius: 999,
                                    border: active
                                        ? "1px solid var(--accent-primary)"
                                        : "1px solid var(--border-color)",
                                    background: active ? "var(--accent-primary)" : "var(--bg-secondary)",
                                    color: active ? "#fff" : "var(--text-secondary)",
                                    cursor: "pointer",
                                    fontSize: 13,
                                    fontWeight: active ? 600 : 400,
                                }}
                            >
                                {a.name}
                            </button>
                        );
                    })}
                </div>
            </div>

            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                    gap: 12,
                    marginBottom: 20,
                }}
            >
                {summaries.map((s) => (
                    <div
                        key={s.sku}
                        style={{
                            background: "var(--bg-secondary)",
                            border: "1px solid var(--border-color)",
                            borderRadius: 8,
                            padding: 14,
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                                color: "var(--text-secondary)",
                                fontSize: 12,
                                marginBottom: 4,
                            }}
                        >
                            <Euro size={14} /> {s.name}{" "}
                            <span style={{ color: "var(--text-muted)" }}>({s.sku})</span>
                        </div>
                        <div style={{ fontSize: 22, fontWeight: 600, color: "var(--text-primary)" }}>
                            €
                            {s.cost.toLocaleString("en-IE", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                            })}
                        </div>
                        <div style={{ color: "var(--text-secondary)", fontSize: 13, marginTop: 2 }}>
                            {s.hours.toFixed(1)} hrs · {s.entryCount}{" "}
                            {s.entryCount === 1 ? "entry" : "entries"}
                        </div>
                    </div>
                ))}
                <div
                    style={{
                        background: "var(--bg-tertiary)",
                        border: "1px solid var(--accent-primary)",
                        borderRadius: 8,
                        padding: 14,
                    }}
                >
                    <div
                        style={{
                            color: "var(--accent-primary)",
                            fontSize: 12,
                            marginBottom: 4,
                            fontWeight: 600,
                            letterSpacing: 0.3,
                        }}
                    >
                        TOTAL · ALL ACQUISITIONS
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 600, color: "var(--text-primary)" }}>
                        €
                        {totals.cost.toLocaleString("en-IE", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                        })}
                    </div>
                    <div style={{ color: "var(--text-secondary)", fontSize: 13, marginTop: 2 }}>
                        {totals.hours.toFixed(1)} hrs · {totals.entryCount}{" "}
                        {totals.entryCount === 1 ? "entry" : "entries"}
                    </div>
                </div>
            </div>

            {error && (
                <div
                    style={{
                        background: "var(--bg-secondary)",
                        border: "1px solid var(--status-error, #d9534f)",
                        borderRadius: 6,
                        padding: "10px 14px",
                        marginBottom: 12,
                        color: "var(--status-error, #d9534f)",
                    }}
                >
                    {error}
                </div>
            )}

            <div
                style={{
                    background: "var(--bg-secondary)",
                    borderRadius: 8,
                    border: "1px solid var(--border-color)",
                }}
            >
                <DataTable
                    columns={columns}
                    data={rows}
                    customStyles={customStyles}
                    pagination
                    paginationPerPage={25}
                    paginationRowsPerPageOptions={[10, 25, 50, 100]}
                    highlightOnHover
                    defaultSortFieldId={1}
                    noDataComponent={
                        <div style={{ padding: 40, textAlign: "center", color: "var(--text-muted)" }}>
                            {entries.length === 0
                                ? "No acquisition time entries for the selected date range."
                                : "No entries match the current acquisition filter."}
                        </div>
                    }
                />
            </div>
        </div>
    );
};

export default AcquisitionChargebacks;
